import { isRecord, Record, type RecordOf } from 'immutable';

import {
  type DynamicRangeIntervalTimestep,
  type GeneratedReportRangeInput,
  type GeneratedReportRanges,
  normalizeGeneratedReportRange,
  type Timestep,
} from 'app-components/utils/date';

import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import type { DeductionType } from '../mileage-tracker/mileageTrackerDeductionForm';

export const REDUCER_KEY = 'reportConfigurationsStore';
export type REDUCER_KEY = typeof REDUCER_KEY;

// ================================================================================================
// ReportConfigFilters - Report Configuration Filters (Self-contained filter types)
// ================================================================================================

export type ReportConfigTagFilterItemProps = {
  type: 'TAG';
  tags: {
    id: string;
  }[];
};

export type ReportConfigPayeeFilterItemProps = {
  type: 'PAYEE';
  payees: string[];
};

export type ReportConfigChartOfAccountFilterItemProps = {
  type: 'CHARTOFACCOUNT';
  chartOfAccounts: {
    type:
      | 'ACCOUNT'
      | 'BALANCE_ADJUSTMENT'
      | 'CATEGORY'
      | 'UNCATEGORIZED'
      | 'CATEGORY_USAGE_TYPE'
      | 'DISPLAY_NODE'
      | 'NONE'
      | 'USAGE_TYPE';
    id: string;
  }[];
};

export type ReportConfigAccountFilterItemProps = {
  type: 'ACCOUNT';
  accounts: { id: string }[];
};

export type ReportConfigUsageFilterItemProps = {
  type: 'USAGE';
  usages: { id: string; type: string }[];
};

export type ReportConfigCustomerProjectFilterItemProps = {
  type: 'CUSTOMERPROJECT';
  customerProjects: { customer: string; projects: string[] }[];
};

export type ReportConfigAdvancedFilterItemProps = {
  type: 'ADVANCED';
  billsAndSubscriptions?: boolean;
  excludedInReports?: boolean;
  excludedInF2S?: boolean;
  reviewed?: boolean;
};

export type ReportConfigFilterItemProps =
  | ReportConfigTagFilterItemProps
  | ReportConfigPayeeFilterItemProps
  | ReportConfigChartOfAccountFilterItemProps
  | ReportConfigAccountFilterItemProps
  | ReportConfigUsageFilterItemProps
  | ReportConfigCustomerProjectFilterItemProps
  | ReportConfigAdvancedFilterItemProps;

export type ReportConfigFilterProps = {
  isDeleted?: boolean;
  type?: string;
  id?: string;
  parentIds?: string[];
  filterItems?: ReportConfigFilterItemProps[];
  clientId?: string;
};

export type ReportConfigFilters = RecordOf<ReportConfigFilterProps>;

export const mkReportConfigFilters = Record<ReportConfigFilterProps>({
  id: undefined,
  isDeleted: undefined,
  type: undefined,
  parentIds: undefined,
  filterItems: undefined,
  clientId: undefined,
});

export type AccountingType = 'ACCRUAL' | 'CASH';
export type ReportType = 'COMPARISON' | 'SUMMARY' | 'TRANSACTION';
export type ReportSubType =
  | 'BALANCE_SHEET'
  | 'BALANCES_OVER_TIME'
  | 'CUSTOM'
  | 'INCOME'
  | 'NET_INCOME'
  | 'PERFORMANCE_OVER_TIME'
  | 'SPENDING'
  | 'NET_WORTH'
  | 'TAX_SCHEDULE'
  | 'TAX_SUMMARY'
  | 'SAVINGS'
  | 'NONE';

// export type DateRange =
//     | 'ALL_DATES'
//     | 'THIS_MONTH'
//     | 'THIS_QUARTER'
//     | 'THIS_YEAR'
//     | 'LAST_MONTH'
//     | 'LAST_QUARTER'
//     | 'LAST_YEAR'
//     | 'MONTH_TO_DATE'
//     | 'QUARTER_TO_DATE'
//     | 'YEAR_TO_DATE'
//     | 'RECENT_7_DAYS'
//     | 'RECENT_14_DAYS'
//     | 'RECENT_2_MONTHS'
//     | 'RECENT_3_MONTHS'
//     | 'RECENT_6_MONTHS'
//     | 'RECENT_12_MONTHS'
//     | 'CUSTOM';

// ================================================================================================
// Comparison ranges - multi-range comparison reports (COMPARISON report type)
// ================================================================================================

/** Derived columns a comparison range can contribute in addition to its raw `VALUE` column. */
export type AdditionalComparisonColumnType = 'CUMULATIVE_VALUE' | 'DELTA_AMOUNT' | 'DELTA_PERCENTAGE';

/** The primary range has no other range to delta against, so only a running total makes sense. */
export type PrimaryAdditionalColumnType = 'CUMULATIVE_VALUE';

/**
 * `AUTO` means the server generates the display label from the resolved range.
 *
 * The QCS design spec calls this variant `PREDEFINED`, but the implemented service rejects that
 * value — its `ComparisonRange$LabelType` enum accepts only `CUSTOM` and `AUTO`, and a request
 * carrying `PREDEFINED` fails deserialization with QCS-0400-2. The running service wins.
 */
export type ComparisonRangeLabel = { type: 'AUTO' } | { type: 'CUSTOM'; value: string };

/**
 * Comparison ranges accept the standard report ranges plus two modes that are resolved
 * relative to the primary range. Deliberately a separate union from `GeneratedReportRanges`:
 * widening that type would force new entries in the exhaustive label maps in
 * `utils/date/predefinedRange.ts` and leak these modes into every non-report date picker.
 */
export type ComparisonRangeDateRange = GeneratedReportRanges | 'PRIOR_PERIOD' | 'PRIOR_YEAR_PERIOD';

/** Reserved id for the primary range; never stored as a `ComparisonRange`. */
export const PRIMARY_RANGE_ID = 'PRIMARY' as const;

export type ComparisonRange = {
  /** Stable across edits — column identity depends on it. Never derive it from `label`. */
  rangeId: string;
  label: ComparisonRangeLabel;
  dateRange: ComparisonRangeDateRange;
  startOn?: string;
  endOn?: string;
  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;
  /** Order is significant — it determines generated column order. */
  additionalColumnTypes?: AdditionalComparisonColumnType[];
};

export type ComparisonRangeInput = Omit<ComparisonRange, 'dateRange'> & {
  dateRange: GeneratedReportRangeInput | 'PRIOR_PERIOD' | 'PRIOR_YEAR_PERIOD';
};

export type RowType =
  | 'ACCOUNT'
  | 'BUSINESS'
  | 'CATEGORY'
  | 'CLIENT'
  | 'PROJECT'
  | 'PAYEE'
  | 'TAG'
  | 'TAX_LINE_ITEM'
  | 'TAX_SCHEDULE'
  | 'TIME'
  | 'USAGE'
  | 'NONE';
export type ColumnType =
  | 'ACCOUNT'
  | 'BUSINESS'
  | 'CLIENT'
  | 'PAYEE'
  | 'PROJECT'
  | 'TAG'
  | 'TIME'
  | 'TOTAL'
  | 'USAGE'
  | 'NONE';

export type ChartType =
  | 'AREA'
  | 'BAR'
  | 'BAR_GROUPED'
  | 'BAR_GROUPED_HORIZONTAL'
  | 'BAR_SIDEWAYS'
  | 'BAR_STACKED'
  | 'MULTILINE'
  | 'NET_INCOME'
  | 'PIE'
  | 'SANKEY'
  | 'WATERFALL'
  | 'NONE';

export type PersistentType = 'PREDEFINED' | 'CUSTOM' | 'TEMPORARY' | 'OPEN' | 'SAVED';
export type DensityType = 'COMPACT' | 'STANDARD' | 'COMFORTABLE';
export type DeltaDisplayMode = 'ARROW' | 'SIGN';
export const DEFAULT_DELTA_DISPLAY_MODE: DeltaDisplayMode = 'ARROW';
export type GraphType = 'GRAPH_AND_GRID' | 'GRID' | 'GRAPH';
export type ChartView = 'ASSETS_VS_LIABILITIES' | 'ACCOUNT_GROUPS' | 'INDIVIDUAL_ACCOUNTS';
export const DEFAULT_CHART_VIEW: ChartView = 'ASSETS_VS_LIABILITIES';

export type ReportGridColumnOption = {
  field: string;
  visible: boolean;
};

export type DisplayOptionsType = {
  hideCents: boolean;
  showExpensesAsNegative: boolean;
  deltaDisplayMode?: DeltaDisplayMode;
  alternateRows?: boolean;
  collapseAll?: boolean | null;
  density?: DensityType;
  showOtherOnTop?: boolean;
  showSubTotalsOnTop?: boolean;
  showPLGraph?: boolean; // from pref
  showRowNumbers?: boolean;
  startAtZero?: boolean;
  showExcludedTxns?: boolean;
  showExcludedAccounts?: boolean;
  chartView?: ChartView;
  columns?: ReportGridColumnOption[];
};

export type OptionsType = {
  viewToShow?: GraphType;
  showInterval?: boolean;
  hideReportHeader?: boolean;
  showViewToShow?: boolean;
  showBreakdownBy?: boolean;
  showViewBy?: boolean;
};

export type ReportNameType =
  | 'total-spending'
  | 'monthly-spending'
  | 'Spending Summary'
  | 'Uncategorized Spending'
  | 'Last 3 Months of Spending'
  | 'income'
  | 'Income Summary'
  | 'income-expense'
  | 'net-income'
  | 'savings'
  | 'net-worth'
  | 'net-worth-page'
  | 'monthly-summary'
  | 'refund-tracker'
  | 'credit-score'
  | 'taxes'
  | `taxes-${string}`
  | 'personal-taxes'
  | 'custom'
  | 'profit-loss'
  | 'balance-sheet'
  | `balance-sheet-${string}`
  | 'spending-dashboard';

export type ReportConfigurationProps = {
  id?: string;
  clientId?: string;

  name: ReportNameType;
  usage?: string[];
  description?: string;

  accountingType: AccountingType;
  deductionType?: DeductionType.STANDARD | DeductionType.ACTUAL;
  reportType: ReportType;
  reportSubType: ReportSubType;
  persistentType: PersistentType;
  options?: OptionsType;
  displayOptions?: DisplayOptionsType;

  dateRange: GeneratedReportRanges;
  startOn?: string;
  endOn?: string;

  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;

  rowType: RowType;
  columnType: ColumnType;
  timestep?: Timestep;

  // this is persisted in QCS
  filterId?: string;

  // this is only used as transient data in QCS by the generated-report APIs
  filter?: ReportConfigFilters | null;

  source?: string;

  predefinedConfigId?: string;
  parentConfigId?: string;
  parentPersistentType?: PersistentType;
  isParentDeleted?: boolean;

  /** Server-assigned display order for PREDEFINED configs. */
  order?: number;
  /** Server-controlled visibility for global PREDEFINED configs. */
  isHidden?: boolean;

  /**
   * Ordered comparison ranges for COMPARISON reports. Order is significant.
   * Always write `[]` rather than `undefined` to clear — see the note on the Record below.
   */
  comparisonRanges?: ComparisonRange[];
  primaryAdditionalColumnTypes?: PrimaryAdditionalColumnType[];
  showSubTotalLabel?: boolean;
  showSubTotalsOnBottom?: boolean;
  showTotalRows?: boolean;
  isDeleted?: boolean;
  modifiedAt?: string;
  dbVersion?: number;
  chartType?: ChartType;

  // this is only used by client
  location?: string;
};

export type ReportConfigurationInputProps = Omit<ReportConfigurationProps, 'dateRange' | 'comparisonRanges'> & {
  dateRange: GeneratedReportRangeInput;
  comparisonRanges?: ComparisonRangeInput[];
};

export type ReportConfiguration = RecordOf<ReportConfigurationProps>;
export const ReportConfigurationRecord = Record<ReportConfigurationProps>({
  id: undefined,
  clientId: undefined,

  name: undefined!,
  description: undefined,
  usage: undefined,

  accountingType: 'CASH',
  deductionType: undefined,
  reportType: undefined!,
  reportSubType: undefined!,
  persistentType: 'TEMPORARY',
  options: undefined,
  displayOptions: undefined,
  chartType: undefined,

  dateRange: undefined!,
  startOn: undefined,
  endOn: undefined,

  dateRangeInterval: undefined,
  dateRangeIntervalCount: undefined,

  columnType: undefined!,
  rowType: undefined!,
  timestep: undefined,

  filterId: undefined,
  filter: undefined,

  source: undefined,

  predefinedConfigId: undefined,
  parentConfigId: undefined,
  parentPersistentType: undefined,
  isParentDeleted: undefined,

  order: undefined,
  isHidden: undefined,

  // Every key below must also exist on ReportConfigurationProps, and — more importantly — every
  // optional prop must be repeated HERE. Immutable silently ignores any key outside a Record's
  // declared shape, and `Record<T>({...})` does not require optional members, so TypeScript will
  // not catch an omission. The legacy `compareDateRange`/`compareStartOn`/`compareEndOn` props
  // were declared but never listed here, so they never round-tripped despite being cloned.
  comparisonRanges: undefined,
  primaryAdditionalColumnTypes: undefined,

  isDeleted: undefined,
  modifiedAt: undefined,
  dbVersion: undefined,

  showSubTotalsOnBottom: undefined,
  showSubTotalLabel: undefined,
  showTotalRows: undefined,

  location: undefined,
});
const normalizeComparisonRange = (range: ComparisonRangeInput): ComparisonRange => ({
  ...range,
  dateRange:
    range.dateRange === 'PRIOR_PERIOD' || range.dateRange === 'PRIOR_YEAR_PERIOD'
      ? range.dateRange
      : normalizeGeneratedReportRange(range.dateRange),
});

export const normalizeReportConfigurationDateRanges = (
  props: ReportConfigurationInputProps | ReportConfiguration,
): ReportConfigurationProps => {
  const input = isRecord(props) ? (props.toJS() as ReportConfigurationInputProps) : props;
  return {
    ...input,
    dateRange: normalizeGeneratedReportRange(input.dateRange),
    comparisonRanges: input.comparisonRanges?.map(normalizeComparisonRange),
  };
};

export const mkReportConfiguration = (
  props: ReportConfigurationInputProps | ReportConfiguration,
): ReportConfiguration => ReportConfigurationRecord(normalizeReportConfigurationDateRanges(props));

export type OpenReportConfigurationPayload = {
  parentConfigId: string;
  parentPersistentType?: PersistentType;
};

export type SaveReportConfigurationPayload = {
  id: string;
  name: string;
  saveAsNew: boolean;
};

export type DeleteSavedReportConfigurationPayload = {
  id: string;
};

export type RenameReportConfigurationPayload = {
  id: string;
  name: string;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [REDUCER_KEY]: QcsSyncResourceStore<ReportConfigurationProps>;
  }
}

// ## - Spending Dashboard types
// - - - - - - - - - - - - - - - - - - - - - - - -

/** Tab key for Month / Quarter / Year view on the Spending dashboard. */
export type SpendingTabOptions = 'MONTH' | 'QUARTER' | 'YEAR';

/** Row grouping for the spending breakdown transaction report. */
export type BreakdownGroup = Extract<RowType, 'CATEGORY' | 'PAYEE' | 'TAG'>;

export const SpendingDisplayOptions = {
  MONTH: { dateRange: 'RECENT_12_MONTHS', timestep: 'MONTH' },
  QUARTER: { dateRange: 'RECENT_12_MONTHS', timestep: 'QUARTER' },
  YEAR: { dateRange: 'ALL_DATES', timestep: 'YEAR' },
} as const;
